import axios from 'axios';
import {
  CycleSettingsInput, CycleStatus, EventsPage, PrepareYearResult, RunItemResult, RunItemsPage, RunsPage,
  SaveCycleSettingsResult, SaveSettingsResult, SortState, SwitchYearResult, SyncResult, TimesheetFilters,
  TimesheetListResponse, TimesheetMeta, TimesheetRun, TimesheetSettings, TimesheetSummary
} from './types';

/** Основной бэкенд: сервис Google Drive (порт 3010) для портала недоступен напрямую, запросы идут через /api/v2/timesheet */
const BASE = '/api/v2/timesheet';

export const timesheetApi = {
  async meta(signal?: AbortSignal): Promise<TimesheetMeta> {
    const res = await axios.get(`${BASE}/meta`, { signal });
    return res.data;
  },

  async summary(signal?: AbortSignal): Promise<TimesheetSummary> {
    const res = await axios.get(`${BASE}/summary`, { signal });
    return res.data;
  },

  async list(filters: TimesheetFilters, sort: SortState, page: number, limit: number, signal?: AbortSignal): Promise<TimesheetListResponse> {
    const params: Record<string, string> = {
      sortBy: sort.sortBy, sortOrder: sort.sortOrder, page: String(page), limit: String(limit)
    };
    if (filters.search.trim()) params.search = filters.search.trim();
    if (filters.status) params.status = filters.status;
    const res = await axios.get(`${BASE}/files`, { params, signal });
    return res.data;
  },

  /**
   * Ссылка на Google-таблицу табеля; сервер проверяет зоны данных и перехода и пишет открытие в журнал.
   * year - год прошлого табеля (только пока он доступен); без года открывается табель текущего года.
   */
  async openFile(pharmacyNum: string, year?: number): Promise<{ url: string; fileName: string; year: number }> {
    const res = await axios.post(`${BASE}/files/open`, year === undefined ? { pharmacyNum } : { pharmacyNum, year });
    return res.data;
  },

  /** Причина необязательна: единичное создание не требует обоснования */
  async createFile(pharmacyNum: string, reason?: string): Promise<{ status: 'created' | 'exists'; fileName: string }> {
    const res = await axios.post(`${BASE}/files/create`, reason ? { pharmacyNum, reason } : { pharmacyNum });
    return res.data;
  },

  async getSettings(signal?: AbortSignal): Promise<TimesheetSettings> {
    const res = await axios.get(`${BASE}/settings`, { signal });
    return res.data;
  },

  async saveSettings(folderId: string, year: number, reason: string): Promise<SaveSettingsResult> {
    const res = await axios.put(`${BASE}/settings`, { folderId, year, reason });
    return res.data;
  },

  /** Сверка может идти десятки секунд (листинг папки на Drive): общий таймаут axios здесь не нужен */
  async sync(reason?: string): Promise<SyncResult> {
    const res = await axios.post(`${BASE}/sync`, reason ? { reason } : {}, { timeout: 0 });
    return res.data.result;
  },

  async startRun(reason: string): Promise<number> {
    const res = await axios.post(`${BASE}/runs`, { reason });
    return res.data.runId;
  },

  async cancelRun(): Promise<void> {
    await axios.post(`${BASE}/runs/cancel`, {});
  },

  async currentRun(signal?: AbortSignal): Promise<TimesheetRun | null> {
    const res = await axios.get(`${BASE}/runs/current`, { signal });
    return res.data.run || null;
  },

  async runs(page: number, signal?: AbortSignal): Promise<RunsPage> {
    const res = await axios.get(`${BASE}/runs`, { params: { page: String(page) }, signal });
    return res.data;
  },

  /** Результат запуска по аптекам; result - фильтр по исходу, пустая строка - все */
  async runItems(runId: number, result: RunItemResult | '', page: number, signal?: AbortSignal): Promise<RunItemsPage> {
    const params: Record<string, string> = { page: String(page) };
    if (result) params.result = result;
    const res = await axios.get(`${BASE}/runs/${runId}/items`, { params, signal });
    return res.data;
  },

  /** Журнал операций: что произошло, кто, зачем, когда (по 20 на страницу, новые сверху) */
  async events(page: number, signal?: AbortSignal): Promise<EventsPage> {
    const res = await axios.get(`${BASE}/events`, { params: { page: String(page) }, signal });
    return res.data;
  },

  // ---------- годовой цикл (настройки табелей + вся сеть) ----------

  async cycle(signal?: AbortSignal): Promise<CycleStatus> {
    const res = await axios.get(`${BASE}/cycle`, { signal });
    return res.data.cycle;
  },

  async saveCycleSettings(input: CycleSettingsInput, reason: string): Promise<SaveCycleSettingsResult> {
    const res = await axios.put(`${BASE}/cycle/settings`, { ...input, reason }, { timeout: 0 });
    return { changed: res.data.changed, changes: res.data.changes };
  },

  /** Создаёт папку нового года на Drive и запускает создание табелей всей сети; ход - в общем запуске. Причина не нужна: кто и когда нажал, пишется в журнал */
  async prepareYear(): Promise<PrepareYearResult> {
    const res = await axios.post(`${BASE}/cycle/prepare`, {}, { timeout: 0 });
    return { runId: res.data.runId, year: res.data.year, folderId: res.data.folderId, folderCreated: res.data.folderCreated, reused: res.data.reused };
  },

  async switchYear(reason: string): Promise<SwitchYearResult> {
    const res = await axios.post(`${BASE}/cycle/switch`, { reason });
    return { from: res.data.from, to: res.data.to, prevUntil: res.data.prevUntil, missing: res.data.missing };
  }
};

export const isCancelled = (err: unknown): boolean => axios.isCancel(err);
